import {
  Badge,
  Box,
  Center,
  HStack,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  Stack,
  Text,
} from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { skipToken } from '@tanstack/react-query';
import { MoreHorizontal, Pencil, User } from 'lucide-react';
import { iconSize } from '@duke-hq/ui/constants/scalable';
import type { RouterOutputs } from '@duke-hq/svc-api/admin';
import {
  NEW_CONTACT_VALUE,
  type EmployerContactLogFormValues,
} from '@duke-hq/svc-api/admin/trpc/routers/workPlacements/employers/schemas';
import { trpcAdmin } from '../../lib/trpcAdmin';
import { testIds } from '../testIds';
import { contactLogResultMeta, contactLogTypeMeta } from './contactLogTypeMeta';
import { toDateTimeLocalValue } from './dateTimeLocal';

export type ContactLog =
  RouterOutputs['workPlacements']['employers']['listEmployerContactLogs'][number];

function formatOccurredAt(date: Date): string {
  return date.toLocaleString('en-AU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

// A contact that a log links to but which has since been removed from the
// directory. The edit form still lists it (labelled) so its link round-trips
// rather than silently emptying the select.
export type RemovedContactOption = { id: string; name: string };

export function removedContactFromLog(log: ContactLog): RemovedContactOption | null {
  if (!log.contactDeleted || log.contactId == null || log.contactName == null) {
    return null;
  }
  return { id: log.contactId, name: log.contactName };
}

// Seed the Log Contact form when editing an existing entry.
export function logToFormValues(log: ContactLog): Partial<EmployerContactLogFormValues> {
  const contactSelection =
    log.contactId != null ? log.contactId : log.contactFirstName != null ? NEW_CONTACT_VALUE : '';
  return {
    type: log.type,
    occurredAt: toDateTimeLocalValue(log.occurredAt),
    result: log.result ?? '',
    contactSelection,
    newFirstName: log.contactFirstName ?? '',
    newLastName: log.contactLastName ?? '',
    newEmail: log.contactEmail ?? '',
    newPhone: log.contactPhone ?? '',
    addToContacts: false,
    notes: log.notes ?? '',
  };
}

function ContactLogRow({
  log,
  onEdit,
}: {
  log: ContactLog;
  onEdit: () => void;
}): React.ReactElement {
  const meta = contactLogTypeMeta[log.type];
  return (
    <Box
      data-testid={testIds.contactLogItem}
      borderWidth="1px"
      borderColor="border.default"
      borderRadius="lg"
      bg="bg.canvas"
      px={4}
      py={3}
    >
      <Stack gap={1}>
        <HStack justify="space-between" align="center" gap={2} flexWrap="wrap">
          <HStack gap={2} color="fg.muted" fontSize="sm" flexWrap="wrap" flex="1" minW={0}>
            <Icon as={meta.icon} boxSize={iconSize.sm} />
            <Text fontWeight="medium" color="fg.default">
              {meta.label()}
            </Text>
            {log.contactName != null ? (
              <Text>
                {log.contactName}
                {log.contactDeleted ? (
                  <Text as="span" color="fg.muted">
                    {' '}
                    <Trans>(removed from directory)</Trans>
                  </Text>
                ) : null}
              </Text>
            ) : null}
            {log.loggedByName != null ? (
              <HStack gap={1} color="purple.500">
                <Icon as={User} boxSize={iconSize.sm} />
                <Text fontWeight="medium">{log.loggedByName}</Text>
              </HStack>
            ) : null}
            <Text>{formatOccurredAt(log.occurredAt)}</Text>
          </HStack>
          <HStack gap={1} flexShrink={0}>
            {log.result != null ? (
              <Badge
                variant="outline"
                colorScheme="gray"
                borderRadius="full"
                px={2}
                textTransform="none"
                fontWeight="medium"
              >
                {contactLogResultMeta[log.result].label()}
              </Badge>
            ) : null}
            <Menu>
              <MenuButton
                as={IconButton}
                aria-label={t`Contact options`}
                icon={<Icon as={MoreHorizontal} boxSize={iconSize.sm} />}
                size="xs"
                variant="ghost"
                data-testid={testIds.contactLogMenuButton}
              />
              <MenuList>
                <MenuItem
                  icon={<Icon as={Pencil} boxSize={iconSize.sm} />}
                  onClick={onEdit}
                  data-testid={testIds.editContactLogMenuItem}
                >
                  <Trans>Edit</Trans>
                </MenuItem>
              </MenuList>
            </Menu>
          </HStack>
        </HStack>
        {log.notes != null && log.notes !== '' ? (
          <Text fontSize="sm" color="fg.muted">
            {log.notes}
          </Text>
        ) : null}
      </Stack>
    </Box>
  );
}

// Read-only "Recent contacts" history for a directory-linked or tracked employer.
// Logging and editing are driven from the nomination menu (see ReverseMarketingSection);
// this panel just shows the entries and raises edits back up via onEditLog.
export function RecentContactsTimeline({
  employerId,
  studentHasContacted = false,
  studentContactedAt = null,
  onEditLog,
}: {
  employerId: string | null;
  studentHasContacted?: boolean;
  studentContactedAt?: Date | null;
  onEditLog: (log: ContactLog) => void;
}): React.ReactElement {
  const { data: logs, isLoading } =
    trpcAdmin.workPlacements.employers.listEmployerContactLogs.useQuery(
      employerId != null ? { employerId } : skipToken,
    );

  const entries = logs ?? [];
  const hasEntries = entries.length > 0 || studentHasContacted;

  return (
    <Box
      data-testid={testIds.recentContactsPanel}
      borderWidth="1px"
      borderColor="border.default"
      borderRadius="xl"
      bg="bg.subtle"
      p={4}
    >
      <Text fontSize="sm" fontWeight="semibold" mb={3}>
        <Trans>Recent contacts</Trans>
      </Text>

      {isLoading ? (
        <Center py={3}>
          <Spinner size="sm" />
        </Center>
      ) : !hasEntries ? (
        <Text fontSize="sm" color="fg.muted">
          <Trans>No contacts logged yet.</Trans>
        </Text>
      ) : (
        <Stack gap={2}>
          {entries.map((log) => (
            <ContactLogRow key={log.id} log={log} onEdit={() => onEditLog(log)} />
          ))}
          {studentHasContacted ? (
            <Box
              borderWidth="1px"
              borderColor="border.default"
              borderRadius="lg"
              bg="bg.canvas"
              px={4}
              py={3}
            >
              <HStack gap={2} color="fg.muted" fontSize="sm" flexWrap="wrap">
                <Text>
                  <Trans>Contacted by student</Trans>
                </Text>
                {studentContactedAt != null ? (
                  <Text>{formatOccurredAt(studentContactedAt)}</Text>
                ) : null}
              </HStack>
            </Box>
          ) : null}
        </Stack>
      )}
    </Box>
  );
}
